class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener("click", (event) => {
      event.preventDefault();
      const cartItems =
        this.closest("cart-items") || this.closest("cart-drawer-items");
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define("cart-remove-button", CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById("shopping-cart-line-item-status") ||
      document.getElementById("CartDrawer-LineItemStatus");

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener("change", debouncedOnChange.bind(this));

    // Use event delegation to handle upsell toggle events
    // This prevents multiple event listeners when DOM elements are replaced
    if (!CartItems.upsellToggleListenerAttached) {
      document.addEventListener("change", (event) => {
        if (event.target && event.target.id === "cart-upsell-toggle") {
          const activeCartItems = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
          if (activeCartItems && activeCartItems.onCartUpsellToggle) {
            activeCartItems.onCartUpsellToggle(event);
          }
        }
      });
      
      // Listen for cart open events to update toggle state (debounced)
      let cartOpenTimeout;
      document.addEventListener("cart:open", () => {
        clearTimeout(cartOpenTimeout);
        cartOpenTimeout = setTimeout(() => {
          const activeCartItems = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
          if (activeCartItems && activeCartItems.updateCartUpsellToggleState) {
            activeCartItems.updateCartUpsellToggleState().catch(() => {});
          }
        }, 150);
      });
      
      // Listen for cart updated events to update toggle state (debounced)
      let cartUpdatedTimeout;
      document.addEventListener("cart:updated", () => {
        clearTimeout(cartUpdatedTimeout);
        cartUpdatedTimeout = setTimeout(() => {
          const activeCartItems = document.querySelector("cart-items") || document.querySelector("cart-drawer-items");
          if (activeCartItems && activeCartItems.updateCartUpsellToggleState) {
            activeCartItems.updateCartUpsellToggleState().catch(() => {});
          }
        }, 150);
      });
      
      CartItems.upsellToggleListenerAttached = true;
    }
  }

  cartUpdateUnsubscriber = undefined;
  cachedCart = null;
  cacheTimestamp = 0;
  CACHE_DURATION = 2000; // 2 seconds cache

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        if (event.source === "cart-items") {
          return;
        }
        // Cache the cart data from the event if available
        if (event.cartData) {
          this.cachedCart = event.cartData;
          this.cacheTimestamp = Date.now();
        }
        this.onCartUpdate();
      }
    );
    if (this.tagName !== "CART-DRAWER-ITEMS") {
      this.fetchCartAndUpdateState();
    } else {
      // For cart-drawer-items, use cached data or delay initial fetch
      setTimeout(() => {
        this.updateCartUpsellToggleState().catch(() => {});
      }, 100);
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // Centralized cart fetch with caching
  async fetchCartAndUpdateState() {
    // Check if we have fresh cached data
    if (this.cachedCart && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
      this.updateCartUpsellToggleState().catch(() => {});
      this.updateCartUpsellVisibility(this.cachedCart.item_count);
      return;
    }

    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const parsedState = await response.json();
      this.cachedCart = parsedState;
      this.cacheTimestamp = Date.now();
      this.updateCartUpsellToggleState().catch(() => {});
      this.updateCartUpsellVisibility(parsedState.item_count);
    } catch (e) {
      // Silently fail
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = "";

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace(
        "[min]",
        event.target.dataset.min
      );
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace(
        "[max]",
        event.target.max
      );
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace(
        "[step]",
        event.target.step
      );
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute("name"),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === "CART-DRAWER-ITEMS") {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const selectors = ["cart-drawer-items", ".cart-drawer__footer"];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }

          /* Handle empty/non-empty state transition */
          const freshCartDrawer = html.querySelector('cart-drawer');
          const currentCartDrawer = document.querySelector('cart-drawer');
          if (freshCartDrawer && currentCartDrawer) {
            const isEmpty = freshCartDrawer.classList.contains('is-empty');
            currentCartDrawer.classList.toggle('is-empty', isEmpty);

            const drawerInner = currentCartDrawer.querySelector('.drawer__inner');
            const currentEmptyDiv = currentCartDrawer.querySelector('.drawer__inner-empty');
            const freshEmptyDiv = html.querySelector('.drawer__inner-empty');

            if (isEmpty && freshEmptyDiv && !currentEmptyDiv && drawerInner) {
              drawerInner.insertBefore(freshEmptyDiv, drawerInner.firstChild);
            } else if (!isEmpty && currentEmptyDiv) {
              currentEmptyDiv.remove();
            }
          }

          const parsedStateElement = html.querySelector(
            "[data-cart-drawer-state]"
          );
          const parsedState = parsedStateElement
            ? JSON.parse(parsedStateElement.textContent)
            : null;

          if (parsedState) {
            this.cachedCart = parsedState;
            this.cacheTimestamp = Date.now();
            this.updateCartUpsellVisibility(parsedState.item_count);
          }
          this.updateCartUpsellToggleState().catch(() => {});
        })
        .catch(() => {});
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          const sourceQty = html.querySelector("cart-items");
          this.innerHTML = sourceQty.innerHTML;
          const parsedStateElement = html.querySelector("[data-cart-state]");
          const parsedState = parsedStateElement
            ? JSON.parse(parsedStateElement.textContent)
            : null;
          
          if (parsedState) {
            this.cachedCart = parsedState;
            this.cacheTimestamp = Date.now();
            this.updateCartUpsellVisibility(parsedState.item_count);
          }
          this.updateCartUpsellToggleState().catch(() => {});
        })
        .catch(() => {});
    }
  }

  async updateCartUpsellToggleState() {
    const cartUpsellToggle = document.getElementById("cart-upsell-toggle");
    const scriptTag = document.querySelector(
      "script[data-cart-upsell-variant-id]"
    );
    const cartUpsellVariantId = scriptTag
      ? scriptTag.dataset.cartUpsellVariantId
      : "";

    if (!cartUpsellVariantId || !cartUpsellToggle) {
      return;
    }

    // Don't update toggle state if we're in the middle of an operation
    if (this.addingUpsellProduct || this.removingUpsellProduct) {
      return;
    }

    try {
      // Use cached cart data if available and fresh
      let cart;
      if (this.cachedCart && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
        cart = this.cachedCart;
      } else {
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        cart = await cartResponse.json();
        this.cachedCart = cart;
        this.cacheTimestamp = Date.now();
      }
      
      // Find the upsell item by variant ID and properties
      const upsellItem = cart.items.find(item => {
        if (item.variant_id.toString() === cartUpsellVariantId.toString()) {
          if (item.properties && item.properties._upsell === 'true' && item.properties._upsell_type === 'cart_checkbox') {
            return true;
          }
          if (!item.properties || !item.properties._upsell) {
            return true;
          }
        }
        return false;
      });

      const shouldBeChecked = !!upsellItem;
      
      if (cartUpsellToggle.checked !== shouldBeChecked) {
        cartUpsellToggle.checked = shouldBeChecked;
      }
    } catch (error) {
      // Silently fail
    }
  }

  updateCartUpsellVisibility(itemCount) {
    const cartUpsellContainer = document.querySelector(
      ".cart-upsell-toggle-container"
    );
    if (cartUpsellContainer) {
      if (itemCount === 0) {
        cartUpsellContainer.classList.add("hidden");
      } else {
        cartUpsellContainer.classList.remove("hidden");
      }
    }
  }

  onCartUpsellToggle(event) {
    const scriptTag = document.querySelector(
      "script[data-cart-upsell-variant-id]"
    );
    const cartUpsellVariantId = scriptTag
      ? scriptTag.dataset.cartUpsellVariantId
      : "";
    const isChecked = event.target.checked;

    if (!cartUpsellVariantId) {
      event.target.checked = false;
      return;
    }

    if (isChecked) {
      if (!this.addingUpsellProduct) {
        this.addingUpsellProduct = true;
        event.target.disabled = true;
        
        const timeoutId = setTimeout(() => {
          this.addingUpsellProduct = false;
          event.target.disabled = false;
        }, 10000);
        
        this.addUpsellProduct(cartUpsellVariantId).then(() => {
          clearTimeout(timeoutId);
          // Use cached cart data from the add operation
          if (this.cachedCart) {
            this.updateProgressBar({
              total_price: this.cachedCart.total_price,
              items_subtotal_price: this.cachedCart.items_subtotal_price,
              items: this.cachedCart.items,
            });
          }
          this.addingUpsellProduct = false;
          event.target.disabled = false;
        }).catch((error) => {
          clearTimeout(timeoutId);
          event.target.checked = false;
          this.addingUpsellProduct = false;
          event.target.disabled = false;
        });
      } else {
        event.target.checked = false;
      }
    } else {
      if (!this.removingUpsellProduct) {
        this.removingUpsellProduct = true;
        event.target.disabled = true;
        
        const timeoutId = setTimeout(() => {
          this.removingUpsellProduct = false;
          event.target.disabled = false;
        }, 10000);
        
        this.removeUpsellProduct(cartUpsellVariantId).then(() => {
          clearTimeout(timeoutId);
          // Fetch fresh cart data after removal
          fetch(`${routes.cart_url}.js`)
            .then((response) => response.json())
            .then((parsedState) => {
              this.cachedCart = parsedState;
              this.cacheTimestamp = Date.now();
              this.updateProgressBar({
                total_price: parsedState.total_price,
                items_subtotal_price: parsedState.items_subtotal_price,
                items: parsedState.items,
              });
            })
            .catch(() => {})
            .finally(() => {
              this.removingUpsellProduct = false;
              event.target.disabled = false;
            });
        }).catch((error) => {
          clearTimeout(timeoutId);
          this.removingUpsellProduct = false;
          event.target.checked = true;
          event.target.disabled = false;
        });
      } else {
        event.target.checked = true;
      }
    }
  }

  async addUpsellProduct(cartUpsellVariantId) {
    try {
      // Use cached cart if fresh, otherwise fetch
      let cart;
      if (this.cachedCart && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
        cart = this.cachedCart;
      } else {
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        cart = await cartResponse.json();
        this.cachedCart = cart;
        this.cacheTimestamp = Date.now();
      }
      
      // Check if upsell product already exists
      const existingUpsellItem = cart.items.find(item => {
        if (item.variant_id.toString() === cartUpsellVariantId.toString()) {
          if (item.properties && item.properties._upsell === 'true' && item.properties._upsell_type === 'cart_checkbox') {
            return true;
          }
          if (!item.properties || !item.properties._upsell) {
            return true;
          }
        }
        return false;
      });

      if (existingUpsellItem) {
        return;
      }

      const upsellFormData = new FormData();
      upsellFormData.append("id", cartUpsellVariantId);
      upsellFormData.append("quantity", 1);
      upsellFormData.append("properties[_upsell]", "true");
      upsellFormData.append("properties[_upsell_type]", "cart_checkbox");

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      config.body = upsellFormData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      if (!response.ok) {
        throw new Error("Failed to add upsell product");
      }

      // Update cache with fresh data
      const freshCartResponse = await fetch(`${routes.cart_url}.js`);
      this.cachedCart = await freshCartResponse.json();
      this.cacheTimestamp = Date.now();

      this.onCartUpdate();
    } catch (error) {
      throw error;
    }
  }

  async removeUpsellProduct(cartUpsellVariantId) {
    try {
      // Use cached cart if fresh, otherwise fetch
      let cart;
      if (this.cachedCart && (Date.now() - this.cacheTimestamp) < this.CACHE_DURATION) {
        cart = this.cachedCart;
      } else {
        const cartResponse = await fetch(`${routes.cart_url}.js`);
        cart = await cartResponse.json();
      }
      
      // Find the upsell item by variant ID and properties
      const upsellItem = cart.items.find(item => {
        if (item.variant_id.toString() === cartUpsellVariantId.toString()) {
          if (item.properties && item.properties._upsell === 'true' && item.properties._upsell_type === 'cart_checkbox') {
            return true;
          }
          if (!item.properties || !item.properties._upsell) {
            return true;
          }
        }
        return false;
      });

      if (!upsellItem) {
        this.removingUpsellProduct = false;
        return;
      }

      const updateData = {
        updates: {
          [upsellItem.key]: 0
        }
      };

      const response = await fetch(`${routes.cart_update_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error("Failed to remove upsell product");
      }

      // Invalidate cache
      this.cachedCart = null;
      this.cacheTimestamp = 0;

      this.onCartUpdate();
      this.removingUpsellProduct = false;
      
    } catch (error) {
      this.removingUpsellProduct = false;
      throw error;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
      {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section",
      },
      {
        id: "main-cart-footer",
        section: document.getElementById("main-cart-footer").dataset.id,
        selector: ".js-contents",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    /* Prevent quantity changes on auto-gift items (only allow removal via AutoGift script) */
    const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
    if (lineItem && lineItem.dataset.autoGift === 'true' && quantity !== 0) {
      return;
    }

    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        
        // Update cache
        this.cachedCart = parsedState;
        this.cacheTimestamp = Date.now();
        
        const quantityElement =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll(".cart-item");

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute("value");
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle("is-empty", parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        const cartFooter = document.getElementById("main-cart-footer");

        if (cartFooter)
          cartFooter.classList.toggle("is-empty", parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            "is-empty",
            parsedState.item_count === 0
          );

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document
              .getElementById(section.id)
              .querySelector(section.selector) ||
            document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const updatedValue = parsedState.items[line - 1]
          ? parsedState.items[line - 1].quantity
          : undefined;
        let message = "";
        if (
          items.length === parsedState.items.length &&
          updatedValue !== parseInt(quantityElement.value)
        ) {
          if (typeof updatedValue === "undefined") {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace(
              "[quantity]",
              updatedValue
            );
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) ||
          document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(
                cartDrawerWrapper,
                lineItem.querySelector(`[name="${name}"]`)
              )
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector(".drawer__inner-empty"),
            cartDrawerWrapper.querySelector("a")
          );
        } else if (document.querySelector(".cart-item") && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper,
            document.querySelector(".cart-item__name")
          );
        }

        this.updateProgressBar({
          total_price: parsedState.total_price,
          items_subtotal_price: parsedState.items_subtotal_price,
          items: parsedState.items,
        });

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-items",
          cartData: parsedState,
          variantId: variantId,
        });
        this.updateCartUpsellToggleState().catch(() => {});
        this.updateCartUpsellVisibility(parsedState.item_count);
      })
      .catch(() => {
        this.querySelectorAll(".loading__spinner").forEach((overlay) =>
          overlay.classList.add("hidden")
        );
        const errors =
          document.getElementById("cart-errors") ||
          document.getElementById("CartDrawer-CartErrors");
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateProgressBar({ total_price, items_subtotal_price, items }) {
    const progressWrapper = document.getElementById("cart-progress-wrapper");
    if (!progressWrapper) return;

    /* Excecao a regra de AOV: assinatura no carrinho = barra completa (frete gratis). */
    const hasSubscription = Array.isArray(items)
      ? items.some((item) => item.selling_plan_allocation)
      : progressWrapper.dataset.hasSubscription === "true";
    progressWrapper.dataset.hasSubscription = hasSubscription;

    let cartTotalCents =
      progressWrapper.dataset.useItemsSubtotal === "true"
        ? items_subtotal_price
        : total_price;

    const currencyFormat = progressWrapper.dataset.currencyFormat;
    const thresholds = progressWrapper.dataset.thresholds
      .split(",")
      .map(Number);
    const preGoalMessages = progressWrapper.dataset.preGoalMessages.split("||");
    const postGoalMessages =
      progressWrapper.dataset.postGoalMessages.split("||");
    const goalPositions = progressWrapper.dataset.goalPositions
      .split(",")
      .map(Number);

    const totalThreshold = thresholds[thresholds.length - 1];
    if (hasSubscription && cartTotalCents > 0) cartTotalCents = totalThreshold;
    const progressPercentage = Math.min(
      (cartTotalCents / totalThreshold) * 100,
      100
    );

    const progressBar = document.getElementById("cart-progress-bar");
    const goalIcons = document.querySelectorAll(".goal-icon");
    const goalMessageElement = document.querySelector(".goal-message");

    if (cartTotalCents === 0) {
      progressWrapper.style.display = "none";
      goalMessageElement.style.display = "none";
      progressBar.style.width = "0%";
    } else {
      progressWrapper.style.display = "block";
      const previousWidth = parseFloat(progressBar.style.width) || 0;
      progressBar.style.width = `${progressPercentage}%`;

      if (progressPercentage >= 100) {
        progressWrapper.classList.add("full");
      } else {
        progressWrapper.classList.remove("full");
      }

      let nextGoalIndex = -1;
      for (let i = 0; i < thresholds.length; i++) {
        if (cartTotalCents < thresholds[i]) {
          nextGoalIndex = i;
          break;
        }
      }

      goalIcons.forEach((goalIcon, index) => {
        const cartTotalDiff = cartTotalCents - thresholds[index];
        const icon = goalIcon.querySelector("img");
        const goalNumber = goalIcon.dataset.index;

        if (icon) {
          if (cartTotalDiff < 0) {
            const regularIconUrl = goalIcon.dataset.regularIcon;
            if (regularIconUrl) {
              icon.src = regularIconUrl;
              icon.srcset = `${regularIconUrl} 50w`;
              icon.alt = `Goal ${goalNumber}`;
            }
          } else {
            const reachedIconUrl = goalIcon.dataset.reachedIcon;
            if (reachedIconUrl) {
              icon.src = reachedIconUrl;
              icon.srcset = `${reachedIconUrl} 50w`;
              icon.alt = `Goal ${goalNumber} Reached`;
            }
          }
        }
      });

      goalMessageElement.style.display = "block";
      if (nextGoalIndex === -1) {
        const message = postGoalMessages[postGoalMessages.length - 1];
        goalMessageElement.innerHTML = message;
      } else {
        const remainingForGoal = thresholds[nextGoalIndex] - cartTotalCents;
        const remainingAmount = remainingForGoal / 100;
        const remainingAmountFormatted = this.formatCurrency(
          currencyFormat,
          remainingAmount
        );
        const preGoalMessageTemplate = preGoalMessages[nextGoalIndex];
        const message = preGoalMessageTemplate.replace(
          "[x]",
          remainingAmountFormatted
        );
        goalMessageElement.innerHTML = message;
      }
    }
  }

  formatCurrency(currencyFormat, amount) {
    let formattedAmount = "";
    formattedAmount = currencyFormat
      .replace("{{amount}}", amount.toFixed(2))
      .replace("{{amount_no_decimals}}", amount.toFixed(0))
      .replace(
        "{{amount_with_comma_separator}}",
        amount.toFixed(2).replace(".", ",")
      )
      .replace(
        "{{amount_no_decimals_with_comma_separator}}",
        amount.toFixed(0).replace(".", ",")
      )
      .replace(
        "{{amount_with_apostrophe_separator}}",
        amount.toFixed(2).replace(".", "'")
      )
      .replace(
        "{{amount_no_decimals_with_space_separator}}",
        amount.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      )
      .replace(
        "{{amount_with_space_separator}}",
        amount
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ")
          .replace(".", ",")
      )
      .replace(
        "{{amount_with_period_and_space_separator}}",
        amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
      );
    return formattedAmount;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError)
      lineItemError.querySelector(".cart-item__error-text").textContent =
        message;

    this.lineItemStatusElement.setAttribute("aria-hidden", true);

    const cartStatus =
      document.getElementById("cart-live-region-text") ||
      document.getElementById("CartDrawer-LiveRegionText");
    cartStatus.setAttribute("aria-hidden", false);

    setTimeout(() => {
      cartStatus.setAttribute("aria-hidden", true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.add("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) =>
      overlay.classList.remove("hidden")
    );

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute("aria-hidden", false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById("main-cart-items") ||
      document.getElementById("CartDrawer-CartItems");
    mainCartItems.classList.remove("cart__items--disabled");

    const cartItemElements = this.querySelectorAll(
      `#CartItem-${line} .loading__spinner`
    );
    const cartDrawerItemElements = this.querySelectorAll(
      `#CartDrawer-Item-${line} .loading__spinner`
    );

    cartItemElements.forEach((overlay) => overlay.classList.add("hidden"));
    cartDrawerItemElements.forEach((overlay) =>
      overlay.classList.add("hidden")
    );
  }
}

customElements.define("cart-items", CartItems);

if (!customElements.get("cart-note")) {
  customElements.define(
    "cart-note",
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          "input",
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body },
            });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
